import React, { useRef, useEffect } from 'react';
import { SchoolProps } from '../types/SchoolProps';
import { SchoolListingProps } from '../types/SchoolListingProps';
import { ChevronDown } from './icons/ChevronDown';
import { ChevronUp } from './icons/ChevronUp';
import { Button } from './Button';
import { ArrowRight } from './icons/ArrowRight';
import { Link } from './icons/Link';
import { Phone } from './icons/Phone';
import { Close } from './icons/Close';

export const SchoolListing = ({
  school,
  listingType,
  filters,
  closeInfo,
  search,
  onEnquire,
  className,
  scrollToListing,
  isExpanded,
  toggleExpanded,
}: SchoolListingProps) => {
  const listingRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isExpanded && listingRef.current) {
      scrollToListing?.(listingRef);
    }
  }, [isExpanded]);

  const highlightSearchText = (text: string) => {
    if (!search || !text) return text;
    // Split text on highlight term, include term itself into parts, ignore case
    const parts = text.split(new RegExp(`(${search})`, 'gi'));
    return (
      <span>
        {parts.map((part: string, index: number) =>
          part.toLowerCase() === search.toLowerCase() ? (
            <b key={index}>{part}</b>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };

  const handleEnquire = (e: React.MouseEvent, school: SchoolProps) => {
    e.stopPropagation();
    onEnquire(school);
  }
  
  return (
    <div
      ref={listingRef}
      className={`${className} bg-white md:hover:bg-[#F7F7F7] ${
        listingType === 'map'
          ? 'md:min-w-[350px] p-5 shadow-school-listing rounded-2xl'
          : 'px-4 py-7 border-b border-custom-border cursor-pointer'
      }`}
      onClick={toggleExpanded}>
      <div className='flex justify-between'>
        <h1
          className={`font-bold mb-2 ${
            listingType === 'map' ? 'max-w-[270px] text-sm' : 'max-w-[392px]'
          }`}>
          {highlightSearchText(school.school)}
          {filters && filters.length > 0 && (
            <span
              className={`text-sm font-normal text-gray-500 ${
                listingType === 'map' ? 'block' : ''
              }`}>
              {filters.map((tag: string, index: number) => (
                <span key={index}>
                  {' '}
                  {index === 0 ? '(' : ''}
                  {tag}
                  {index < filters.length - 1 ? ', ' : ')'}
                </span>
              ))}
            </span>
          )}
        </h1>
        {listingType === 'map' ? (
          <div className='cursor-pointer' onClick={closeInfo}>
            <Close variant='dark' />
          </div>
        ) : (
          <div className='cursor-pointer'>
            {isExpanded ? (
              <ChevronUp variant='dark' />
            ) : (
              <ChevronDown variant='dark' />
            )}
          </div>
        )}
      </div>
      <p className='text-kashmir-blue'>{highlightSearchText(school.address)}</p>
      {(isExpanded === true || listingType === 'map') && (
        <div
          className={`mt-4 ${
            listingType === 'map' ? 'flex justify-between items-center' : ''
          }`}>
          <p
            className={`flex ${
              listingType === 'map' ? 'flex-col items-center' : ''
            }`}>
            <Link variant='dark' className='mr-2 mb-2' />
            <a
              href={`http://${school.website}`}
              onClick={(e) => e.stopPropagation()}
              className='underline underline-offset-2'
              target='_blank'
              rel='noopener noreferrer'
              >
              {listingType === 'map' ? 'website' : school.website}
            </a>
          </p>
          <p
            className={`flex ${
              listingType === 'map' ? 'flex-col items-center justify-end' : ''
            }`}>
            <Phone variant='dark' className='mr-2 mb-2' />
            <a
              href={`tel:${school.phone}`}
              onClick={(e) => e.stopPropagation()}
              className='underline underline-offset-2'>
              {listingType === 'map' ? 'call school' : school.phone}
            </a>
          </p>
          <Button
            onClick={(e) => handleEnquire(e as React.MouseEvent, school)}
            colour='golden'
            label={`${
              listingType === 'map' ? 'Enquire' : 'Enquire about this school'
            }`}
            className={`${listingType === 'map' ? '' : 'w-full mt-7'}`}>
            <ArrowRight variant='dark' className='ml-3' />
          </Button>
        </div>
      )}
    </div>
  );
};